import axios from 'axios'
import {getAdminToken} from '../auth/_requests'

const API_URL = process.env.REACT_APP_API_URL

const adminApi = axios.create({withCredentials: true})
adminApi.interceptors.request.use((config) => {
  const token = getAdminToken()
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

export interface VirtualNumberAccount {
  _id: string
  first_name?: string
  last_name?: string
  company_name?: string
  email?: string
  username?: string
  phone?: string
  // The account's shared pinned notes — the same ones shown on the campaigns page
  // and the payments page. The newest is the one displayed here.
  important_notes?: {_id?: string; text: string; created_at?: string}[]
}

// Add a pinned note to the customer's account. Same endpoint the payments page uses,
// so a note written here shows up everywhere the account's notes are shown.
export function addAccountNote(accountId: string, text: string): Promise<{data: {admin_notes: any[]}}> {
  return adminApi.post(`${API_URL}/admin/account-notes/${accountId}`, {text})
}

// One Facebook profile on the account, with its live connection status.
export interface VirtualNumberProfile {
  _id: string
  first_name?: string
  last_name?: string
  facebook_id?: string
  facebook_username?: string
  last_health_check?: string
  is_logged_in?: boolean
  status: 'connected' | 'disconnected' | 'error'
}

// Mirrors the profile_stats block on the admin payments card.
export interface AccountActivityStats {
  profile_count: number
  active_profiles: number
  campaign_count: number
  post_count: number
  group_count: number
  scheduled_today: number
  posted_today: number
  posted_14d: number
  missed_today: number
  errors_today: number
}

export interface VirtualNumber {
  _id: string
  ddi: string
  paycall_id?: string
  account?: VirtualNumberAccount | null
  forward_number?: string
  enabled?: boolean
  active?: boolean
  forward_synced?: boolean
  assigned_at?: string
  // Is the account this number is attached to actually running? These come from the
  // SAME helper the payments page uses (lib/accountActivityStats.js), so a dormant
  // account holding a number is obvious at a glance and the two screens agree.
  // profiles_connected counts profiles that are genuinely logged in right now, not
  // every profile that happens to hold a facebook_id.
  profiles_connected?: number
  posted_count_14d?: number
  account_stats?: AccountActivityStats | null
  profiles?: VirtualNumberProfile[]
  // When this number first went out in a post: the earliest LeadLink minted for
  // the DID. Different from assigned_at — we may have handed it over weeks before
  // the customer actually started advertising with it.
  first_advertised_at?: string | null
  advertised_posts?: number
  admin_note?: string
  admin_note_updated_at?: string | null
  // Set while the number is inside its post-release quarantine — it can't be
  // handed to anyone until this date (server: lib/virtualNumberQuarantine.js).
  quarantined_until?: string | null
}

export interface AccountSearchResult {
  _id: string
  first_name?: string
  last_name?: string
  company_name?: string
  email?: string
  phone?: string
}

export function getVirtualNumbers(): Promise<{data: {ok: boolean; numbers: VirtualNumber[]}}> {
  return adminApi.get(`${API_URL}/admin/virtual-numbers`)
}

export interface VirtualNumberCounts {
  [ddi: string]: {
    phone_calls: number
    whatsapp_leads: number
    // Inbound marketplace conversations + comments on the account's group posts,
    // over the same date range as the call/lead counts.
    marketplace_inquiries?: number
    post_comments?: number
  }
}

export function getVirtualNumberCounts(
  from?: string,
  to?: string
): Promise<{data: {ok: boolean; counts: VirtualNumberCounts; period: {from: string; to: string}}}> {
  const qs = from && to ? `?from=${from}&to=${to}` : ''
  return adminApi.get(`${API_URL}/admin/virtual-numbers/counts${qs}`)
}

export function syncVirtualNumbers(): Promise<{data: {ok: boolean; synced: number; numbers: VirtualNumber[]}}> {
  return adminApi.post(`${API_URL}/admin/virtual-numbers/sync`, {})
}

export interface VirtualNumberGroup {
  id: string
  name: string
}

export function getVirtualNumberGroups(): Promise<{data: {ok: boolean; groups: VirtualNumberGroup[]; push_enabled: boolean}}> {
  return adminApi.get(`${API_URL}/admin/virtual-numbers/groups`)
}

export function assignVirtualNumber(
  ddi: string,
  data: {account_id: string; forward_number?: string; enabled?: boolean; reassign?: boolean; group_id?: string}
): Promise<{data: {ok: boolean; number: VirtualNumber; forward: any}}> {
  return adminApi.post(`${API_URL}/admin/virtual-numbers/${encodeURIComponent(ddi)}/assign`, data)
}

export function setVirtualNumberNote(
  ddi: string,
  note: string
): Promise<{data: {ok: boolean; admin_note: string; admin_note_updated_at: string | null}}> {
  return adminApi.put(`${API_URL}/admin/virtual-numbers/${encodeURIComponent(ddi)}/note`, {note})
}

export function unassignVirtualNumber(ddi: string): Promise<{data: {ok: boolean}}> {
  return adminApi.post(`${API_URL}/admin/virtual-numbers/${encodeURIComponent(ddi)}/unassign`, {})
}

export interface WhatsappLead {
  id: string
  created_at: string
  lead_name: string
  lead_phone: string
  message: string
  listing_code: string
  listing_text: string
  listing_image: string
  listing_price: number | null
  listing_deleted?: boolean
  matched: boolean
  status: string
  notes: string
}

export function getAccountLeads(accountId: string): Promise<{data: {ok: boolean; leads: WhatsappLead[]}}> {
  return adminApi.get(`${API_URL}/admin/virtual-numbers/account/${accountId}/leads`)
}

export function updateLead(
  id: string,
  body: {status?: string; notes?: string}
): Promise<{data: {ok: boolean; status: string; notes: string}}> {
  return adminApi.patch(`${API_URL}/admin/virtual-numbers/leads/${id}`, body)
}

export function searchAccounts(q: string): Promise<{data: {accounts: AccountSearchResult[]}}> {
  return adminApi.get(`${API_URL}/admin/search-accounts`, {
    params: {q, light: 1, search_all: 1, limit: 20},
  })
}

// ── Signature / profile / strip (admin acts on any account, no impersonation) ──
export interface DidOption {
  _id: string
  ddi: string
  enabled: boolean
}
export interface CampaignSig {
  _id: string
  name: string
  default_signature: string
  default_signature_locked: boolean
}
export interface ProfileSig {
  _id: string
  name: string
  profile_image: string
  facebook_id: string
  default_signature: string
  default_signature_locked: boolean
  virtual_number_id: string
}
export interface StripPost {
  id: string
  name: string
  post_content: string
  post_code: string
  media_url: string
  category: string
}

const base = `${API_URL}/admin/virtual-numbers`

export function adminGetAccountNumbers(accountId: string): Promise<{data: {ok: boolean; numbers: DidOption[]}}> {
  return adminApi.get(`${base}/account/${accountId}/numbers`)
}
export function adminGetCampaigns(accountId: string): Promise<{data: {ok: boolean; campaigns: CampaignSig[]}}> {
  return adminApi.get(`${base}/account/${accountId}/campaigns`)
}
export function adminSaveCampaignSignature(
  campaignId: string,
  data: {default_signature?: string; default_signature_locked?: boolean}
): Promise<{data: {ok: boolean; campaign: CampaignSig}}> {
  return adminApi.post(`${base}/campaign/${campaignId}/signature`, data)
}
export function adminGetProfiles(accountId: string): Promise<{data: {ok: boolean; profiles: ProfileSig[]}}> {
  return adminApi.get(`${base}/account/${accountId}/profiles`)
}
export function adminSaveProfileSignature(
  profileId: string,
  data: {default_signature?: string; default_signature_locked?: boolean}
): Promise<{data: {ok: boolean; profile: Omit<ProfileSig, 'name' | 'virtual_number_id'>}}> {
  return adminApi.post(`${base}/profile/${profileId}/signature`, data)
}
export function adminAssignProfiles(
  accountId: string,
  data: {virtual_number_id: string | null; profile_ids?: string[]; all?: boolean}
): Promise<{data: {ok: boolean; modified: number}}> {
  return adminApi.post(`${base}/account/${accountId}/assign-profiles`, data)
}
export function adminGetPosts(accountId: string, campaignId?: string): Promise<{data: {ok: boolean; posts: StripPost[]}}> {
  const qs = campaignId ? `?campaign_id=${campaignId}` : ''
  return adminApi.get(`${base}/account/${accountId}/posts${qs}`)
}
export function adminSuggestStrip(
  accountId: string,
  data: {campaign_id?: string; post_ids?: string[]}
): Promise<{data: {ok: boolean; suggestions: {id: string; suggested: string | null; error?: string | null}[]}}> {
  return adminApi.post(`${base}/account/${accountId}/posts/suggest`, data)
}
export function adminBulkUpdatePosts(
  accountId: string,
  updates: {post_id: string; post_content: string}[]
): Promise<{data: {ok: boolean; saved: number; skipped: number}}> {
  return adminApi.post(`${base}/account/${accountId}/posts/bulk-update`, {updates})
}
export function adminGenerateSignature(context?: string, campaignId?: string): Promise<{data: {ok: boolean; signature: string}}> {
  return adminApi.post(`${base}/generate-signature`, {context: context || '', campaign_id: campaignId || ''})
}
// Clear per-post reply messages and set ONE campaign-global auto-reply that uses
// the {number}/{wa_link} virtual-number tokens.
export function adminConsolidateReplies(
  accountId: string,
  data: {global_reply: string; campaign_id?: string}
): Promise<{data: {ok: boolean; campaigns_updated: number; posts_cleared: number; backed_up: number; error?: string}}> {
  return adminApi.post(`${base}/account/${accountId}/replies/consolidate`, data)
}
// How much pre-consolidation reply text is still restorable for this account.
export function adminGetReplyBackup(
  accountId: string,
  campaignId?: string
): Promise<{data: {ok: boolean; posts: number; campaigns: number; backed_up_at: string | null; sample: string}}> {
  return adminApi.get(`${base}/account/${accountId}/replies/backup`, {params: campaignId ? {campaign_id: campaignId} : {}})
}
export interface ReplyBulkEditSample {
  id: string
  kind: 'post' | 'campaign'
  post_code: string
  before: string
  after: string
}
// Add / remove / replace a phone number across every post's reply_text in one
// pass. dry_run writes nothing and returns the counts plus before/after samples.
export function adminBulkEditReplies(
  accountId: string,
  data: {
    mode: 'remove' | 'replace' | 'add'
    from?: string
    to?: string
    text?: string
    position?: 'start' | 'end'
    campaign_id?: string
    include_campaign_defaults?: boolean
    dry_run?: boolean
  }
): Promise<{
  data: {
    ok: boolean
    dry_run: boolean
    scanned: number
    changed: number
    campaigns_changed: number
    backed_up: number
    samples: ReplyBulkEditSample[]
    truncated: boolean
    error?: string
  }
}> {
  return adminApi.post(`${base}/account/${accountId}/replies/bulk-edit`, data)
}
// Write the reviewed (and possibly hand-edited) rows from a bulk-edit preview.
export function adminApplyReplyRows(
  accountId: string,
  rows: {id: string; kind: 'post' | 'campaign'; reply_text: string}[]
): Promise<{
  data: {ok: boolean; changed: number; campaigns_changed: number; skipped: number; backed_up: number; error?: string}
}> {
  return adminApi.post(`${base}/account/${accountId}/replies/bulk-apply`, {rows})
}
// Undo of adminConsolidateReplies: put the original per-post replies (with the
// customer's real numbers) and the previous campaign auto-reply back.
export function adminRestoreReplies(
  accountId: string,
  data?: {campaign_id?: string}
): Promise<{data: {ok: boolean; posts_restored: number; campaigns_restored: number; error?: string}}> {
  return adminApi.post(`${base}/account/${accountId}/replies/restore`, data || {})
}
// Reverse of the strip flow: AI re-inserts the given phone number into each post.
export function adminSuggestAddContact(
  accountId: string,
  data: {phone: string; campaign_id?: string; post_ids?: string[]}
): Promise<{data: {ok: boolean; suggestions: {id: string; suggested: string | null; error?: string | null}[]}}> {
  return adminApi.post(`${base}/account/${accountId}/posts/suggest-add`, data)
}
// Teardown: clear campaign signatures + auto-replies and profile signatures.
export function adminRemoveVirtualNumber(
  accountId: string
): Promise<{data: {ok: boolean; campaigns_cleared: number; profiles_cleared: number}}> {
  return adminApi.post(`${base}/account/${accountId}/remove-virtual-number`, {})
}
